/**
 * Anchors shared by the navigation preview modules.
 *
 * The flyout drills between levels with in-place state; the preview stacks the
 * panes down one page instead, so a level-1 row moves the reader rather than
 * swapping a pane. `NavigationParent` is a button with no `href`, so these are
 * hash jumps driven from its click handler rather than real links.
 */

import {sanitizeAnchorId} from '../../utils/utils'

/** The level-1 pane's section. */
export const LEVEL1_ANCHOR = 'navigation-level1'

/**
 * The section a level-1 row jumps to.
 *
 * Sanitized here, since `ModuleContainer` sanitizes the `anchor` it writes as
 * the section's `id` — an authored id carrying capitals or spaces would come
 * out lowercased and hyphenated, and every jump would miss it.
 *
 * @param {string} level2ModuleId - The id the CMS joins level 1 and 2 on.
 * @returns {string}
 */
export const level2Anchor = (level2ModuleId) =>
    sanitizeAnchorId(`navigation-level2-${level2ModuleId}`)

/**
 * The section a level-2 entry drills into, keyed on its title — a level-3
 * subtree is not a module and carries no id of its own.
 *
 * The halves are sanitized apart: an entry with no title leaves a trailing
 * hyphen, which sanitizing the whole would strip, landing on the level-2 pane's
 * own anchor.
 *
 * @param {string} level2ModuleId - The id of the level-2 module it sits under.
 * @param {string} title - The level-2 entry's title.
 * @returns {string}
 */
export const level3Anchor = (level2ModuleId, title) =>
    `${level2Anchor(level2ModuleId)}-${sanitizeAnchorId(title)}`

/**
 * The level-3 anchors of one level-2 module's entries, in their order.
 *
 * Two entries can be authored under the same title, and a repeat is numbered
 * from its second use rather than left to share a DOM id. That takes the whole
 * list, so the pane and the row that jumps to it read the same array.
 *
 * @param {string} level2ModuleId - The id of the level-2 module they sit under.
 * @param {Array} items - The level-2 entries, unwrapped and in render order.
 * @returns {string[]} One anchor per entry, aligned with `items`.
 */
export const level3Anchors = (level2ModuleId, items = []) => {
    const used = new Map()
    return items.map((item) => {
        const anchor = level3Anchor(level2ModuleId, item?.title)
        const seen = (used.get(anchor) || 0) + 1
        used.set(anchor, seen)
        return seen === 1 ? anchor : `${anchor}-${seen}`
    })
}

/**
 * Moves the reader to an anchored section.
 *
 * Scrolls the element rather than assigning the hash, so a jump to the section
 * already named in the URL still moves. The hash is written afterwards, with no
 * history entry.
 *
 * @param {string} anchor - The target section's id.
 */
export const jumpTo = (anchor) => {
    const target = document.getElementById(anchor)
    if (!target) return
    target.scrollIntoView({behavior: 'smooth'})
    window.history.replaceState(null, '', `#${anchor}`)
}
